import Svg07 from "@assets/icons/svg07.svg?react";
import { pxChangeVw } from "@/utils";
import useDayjs from "@/hooks/useDayjs";
import useDateFormat from "@/hooks/useDateFormat";
import useTranslate from "@/hooks/useTranslate";
import { is_en_gl_link, is_es_mx_link, is_tr_link } from "@/i18n/lang";
import classNames from "classnames";
import { speakText } from "@/utils/speakText";
import { homeAtom } from "@/atoms/home.ts";
import { isLongText } from "@/utils/isLongText.ts";
import { getWeekName } from "@/utils/getWeekName.ts";
import { useClsUtil_09 } from "@/hooks/useClsUtil_09.tsx";
import { useStyUtil_03 } from "@/hooks/useStyUtil_03.tsx";

const BaseTenDay: React.FC = () => {
  try {
    const { dayjs, userTimeZone } = useDayjs();
    const { $t } = useTranslate();
    const homeData = useAtomValue(homeAtom);
    const { forecast5day = [], current = {} } = homeData?.data ?? {};
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { shortDate } = useDateFormat();
    const { hostName } = useHostName();
    let columnWidthRatios = [1, 1, 2, 1.5];
    if (isLongText()) {
      columnWidthRatios = [1.5, 1.4, 2.75, 1.5];
    }

    const totalRatio = columnWidthRatios.reduce((total, current) => total + current, 0);
    const { getCls01, getCls02, getCls03, getCls04, getCls05, getCls06, getCls07 } = useClsUtil_09();
    const { getSty01 } = useStyUtil_03({ columnWidthRatios, totalRatio });

    const getWeatherTimePeriod = (pathname: string, _hostName: string, _current: any, item: any) => {
      // 首先检查是否在首页
      if (pathname === "/home") {
        return "Day";
      }

      return (item?.IsDayTime ?? item?.isDayTime) * 1 === 1 ? "Day" : "Night";
    };
    const handleClick = (e: any) => {
      e.preventDefault();
      navigate("/daily");
    };

    const handleSpeakClick = () => {
      const weatherDetails = forecast5day
        .map((item: any) => {
          let date = "";
          if (item?.EpochDateTime) {
            if (userTimeZone) {
              date = dayjs.unix(item.EpochDateTime).tz(userTimeZone).format(shortDate);
            } else {
              date = item.DateShort;
            }
          }
          const weatherText = item?.[getWeatherTimePeriod(pathname, hostName, current, item)]?.WeatherText || "";
          const precipitationProbability =
            item?.[getWeatherTimePeriod(pathname, hostName, current, item)]?.PrecipitationProbability || "";
          const temperatureRange = `${item?.Temperature?.Minimum?.Value} to ${item?.Temperature?.Maximum?.Value} degrees Celsius`;

          return `On ${date}, ${weatherText}. Precipitation Probability: ${precipitationProbability}%. Temperature Range: ${temperatureRange}.`;
        })
        .join(". ");

      const text = `${$t("title01")}. ${weatherDetails}`;
      speakText(text);
    };

    // 格式化日期（优先显示相对日期）
    const getFormattedDate = (timestamp: number) => {
      // 非近三天则使用原有格式
      if (is_en_gl_link() || is_es_mx_link()) {
        return dayjs.unix(timestamp).tz(userTimeZone).format("DD/MM");
      }
      const dateStr = dayjs.unix(timestamp).tz(userTimeZone).format(shortDate);
      return isLongText() ? dateStr.replace(/\//g, ".") : dateStr;
    };

    const TenDayContent = (
      <WeatherContent className={classNames([getCls01()])} onClick={handleSpeakClick}>
        {forecast5day.map((item: any, index: number) => {
          const weekDayName = getWeekName(item.EpochDateTime);
          return (
            <div key={index} className={classNames([getCls02()])}>
              {/* 第1列 */}
              <div className={classNames([`text-center`])} style={getSty01(0)}>
                <p className="font-500">{weekDayName ?? item.Week}</p>
                <p className={classNames(getCls03())}>
                  {item?.EpochDateTime ? getFormattedDate(item.EpochDateTime) : item.DateShort}
                </p>
              </div>
              {/* 第2列 */}
              <div className={classNames(["center"])} style={getSty01(1)}>
                <WeatherImage
                  className={classNames(getCls04())}
                  src={item?.[getWeatherTimePeriod(pathname, hostName, current, item)]?.WeatherIconImg}
                />
              </div>
              {/* 第3列 */}
              <div className={classNames(getCls05())} style={getSty01(2)}>
                {item?.[getWeatherTimePeriod(pathname, hostName, current, item)]?.WeatherText}
              </div>
              {/* 第4列 */}
              <div className="flex justify-end items-center gap-x-0" style={getSty01(3)}>
                <span style={{ display: "flex", columnGap: pxChangeVw("5px"), alignItems: "center" }}>
                  <Svg07
                    style={{
                      width: pxChangeVw("12px"),
                    }}
                  />
                  {is_tr_link()
                    ? "%" + item?.[getWeatherTimePeriod(pathname, hostName, current, item)]?.PrecipitationProbability
                    : item?.[getWeatherTimePeriod(pathname, hostName, current, item)]?.PrecipitationProbability + "%"}
                </span>

                <span className={classNames(getCls06())}>
                  {item?.Temperature?.Maximum?.Value}
                  {item?.Temperature?.Maximum?.Unit}/{item?.Temperature?.Minimum?.Value}
                  {item?.Temperature?.Minimum?.Unit}
                </span>
              </div>
            </div>
          );
        })}
        <span onClick={handleClick} className={classNames(getCls07())}>
          {$t("title03")}
        </span>
      </WeatherContent>
    );
    return TenDayContent;
  } catch (error) {}
};
export default BaseTenDay;
